/**
 * @fileoverview Step Layout Component - Shared wrapper for all ingestion steps.
 *
 * Provides consistent structure with compact density styling (space-y-2, p-2).
 * Each step component should wrap its content in this layout.
 *
 * @example
 * <IngestionStepLayout title="Police Report" description="Enter police report details">
 *   <IngestionNotFoundBanner ... />
 *   <form>...</form>
 * </IngestionStepLayout>
 */
import React from 'react';

interface StepLayoutProps {
  /** Step title displayed as heading */
  title: string;
  /** Brief description shown below title */
  description: string;
  /** Step content (forms, banners, etc.) */
  children: React.ReactNode;
}

/**
 * Shared layout wrapper for ingestion step components.
 *
 * Renders title/description header followed by children.
 * Uses compact density styling per the design system.
 */
export const IngestionStepLayout: React.FC<StepLayoutProps> = ({
  title,
  description,
  children,
}) => {
  return (
    <div className="space-y-2 p-2">
      <div>
        <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
        <p className="text-xs font-medium text-zinc-600">{description}</p>
      </div>

      {children}
    </div>
  );
};
